// FILE: src/components/MessageModal.jsx
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "./Modal";
import {
    collection,
    addDoc,
    serverTimestamp,
    doc,
    setDoc,
    query,
    orderBy,
    onSnapshot,
    getDocs,
    writeBatch,
    updateDoc
} from "firebase/firestore";
import "./css/MessagesInboxModal.css"; // reuse existing styles (bubbles, composer, etc.)

/**
 * MessageModal
 *
 * - Upserts a conversation doc at /messages/{convId} and writes message docs to
 *   /messages/{convId}/items
 * - Shows the existing thread between current user and recipient
 *
 * Props:
 *  - open (bool)
 *  - onClose (func)
 *  - toUid (string)           // recipient uid
 *  - toHandle (string)        // optional recipient handle (for title)
 *  - db (Firestore instance)
 *  - user (firebase auth user)
 *  - useFirebase (bool)
 *  - setToast (func)
 */
export default function MessageModal({
    open,
    onClose,
    toUid,
    toHandle,
    db,
    user,
    useFirebase = false,
    setToast
}) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [thread, setThread] = useState([]); // messages in the conversation (ordered asc)
    const [loadingThread, setLoadingThread] = useState(false);
    const composerRef = useRef(null);

    const currentUid = user?.uid || null;

    // deterministic conversation id generator
    const conversationIdFor = (a, b) => {
        if (!a || !b) return null;
        return [a, b].sort().join("_");
    };

    useEffect(() => {
        if (!open) {
            setText("");
            setThread([]);
            setSending(false);
        }
    }, [open]);

    useEffect(() => {
        // subscribe to conversation subcollection when modal opens and we have uids & firebase
        if (!open) return;
        if (!useFirebase || !db) return;
        if (!currentUid || !toUid) return;

        const convId = conversationIdFor(currentUid, toUid);
        if (!convId) return;

        setLoadingThread(true);
        const itemsRef = collection(db, "messages", convId, "items");
        const q = query(itemsRef, orderBy("createdAt", "asc"));

        const unsub = onSnapshot(
            q,
            (snap) => {
                const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setThread(msgs);
                setLoadingThread(false);
            },
            (err) => {
                console.error("thread listener error:", err);
                setToast?.({ msg: "Failed to load conversation", type: "warning" });
                setLoadingThread(false);
            }
        );

        // Also try to mark inbound messages as read (best-effort)
        (async () => {
            try {
                // batch update unread messages (to: currentUid, from: toUid, read==false)
                const q2 = query(itemsRef, orderBy("createdAt", "asc"));
                const snap2 = await getDocs(q2);
                const batch = writeBatch(db);
                let changed = false;
                snap2.forEach((d) => {
                    const data = d.data();
                    if (data.to === currentUid && data.from === toUid && data.read !== true) {
                        batch.update(doc(db, "messages", convId, "items", d.id), { read: true, readAt: serverTimestamp() });
                        changed = true;
                    }
                });
                if (changed) await batch.commit();
                // attempt to clear unreadCounts on conversation doc (non-atomic, best-effort)
                try {
                    const convRef = doc(db, "messages", convId);
                    await updateDoc(convRef, { [`unreadCounts.${currentUid}`]: 0 }).catch(() => { });
                } catch { }
            } catch (e) {
                // ignore failures — non-critical
            }
        })();

        return () => unsub();
    }, [open, db, currentUid, toUid, useFirebase, setToast]);

    function showToast(msg, type = "info") {
        if (typeof setToast === "function") {
            setToast({ msg, type });
            setTimeout(() => setToast(null), 1600);
        } else {
            console.info("toast:", msg, type);
        }
    }

    function validate() {
        if (!user || !user.uid) return { ok: false, reason: "not-signed-in" };
        if (!toUid) return { ok: false, reason: "no-recipient" };
        const trimmed = String(text || "").trim();
        if (!trimmed) return { ok: false, reason: "empty" };
        if (trimmed.length > 1024) return { ok: false, reason: "too-long" };
        return { ok: true, value: trimmed };
    }

    async function handleSend(e) {
        if (e && e.preventDefault) e.preventDefault();
        const v = validate();
        if (!v.ok) {
            if (v.reason === "not-signed-in") showToast("Sign in to send a message", "warning");
            else if (v.reason === "no-recipient") showToast("Recipient missing", "warning");
            else if (v.reason === "empty") showToast("Message cannot be empty", "warning");
            else if (v.reason === "too-long") showToast("Message too long (max 1024 chars)", "warning");
            return;
        }

        const safeText = v.value.slice(0, 1024);
        if (!useFirebase || !db) {
            // fallback: open mail client and also show local optimistic message in thread
            const mailto = `mailto:?subject=${encodeURIComponent(`Message for @${toHandle || ""}`)}&body=${encodeURIComponent(safeText)}`;
            // optimistic local append
            setThread((prev) => [...prev, { id: `local-${Date.now()}`, from: currentUid, to: toUid, text: safeText, createdAt: new Date().toISOString(), read: false }]);
            setText("");
            window.location.href = mailto;
            showToast("Opened email client (no backend configured)", "info");
            return;
        }

        setSending(true);
        const convId = conversationIdFor(currentUid, toUid);
        const convRef = doc(db, "messages", convId);
        const itemsColRef = collection(db, "messages", convId, "items");

        const messagePayload = {
            from: currentUid,
            to: toUid,
            text: safeText,
            createdAt: serverTimestamp(),
            read: false,
            fromHandle: user?.displayName || user?.handle || null,
            toHandle: toHandle || null
        };

        try {
            // 1) Upsert conversation doc (merge)
            await setDoc(
                convRef,
                {
                    participants: [currentUid, toUid],
                    participantMap: { [currentUid]: true, [toUid]: true },
                    lastMessage: { text: safeText, from: currentUid, to: toUid, createdAt: serverTimestamp() },
                    lastUpdated: serverTimestamp()
                },
                { merge: true }
            );

            // 2) Add message to subcollection
            await addDoc(itemsColRef, messagePayload);

            // 3) optimistic local append (createdAt string until server timestamp resolves via snapshot)
            setThread((prev) => [...prev, { id: `local-${Date.now()}`, ...messagePayload, createdAt: new Date().toISOString() }]);
            setText("");
            composerRef.current?.focus();
            showToast("Message sent", "success");
        } catch (err) {
            console.error("MessageModal send error:", err);
            if (err && err.code === "permission-denied") {
                showToast("Permission denied. Check Firestore rules and sign-in.", "warning");
            } else {
                showToast("Failed to send message", "warning");
            }
        } finally {
            setSending(false);
        }
    }

    function formatDate(ts) {
        if (!ts) return "";
        if (typeof ts.toDate === "function") return ts.toDate().toLocaleString();
        try {
            return new Date(ts).toLocaleString();
        } catch {
            return "";
        }
    }

    if (!open) return null;

    return (
        <Modal
            title={`Message @${toHandle || toUid}`}
            onClose={() => {
                setText("");
                onClose && onClose();
            }}
        >
            <div style={{ minWidth: 360, maxWidth: 720 }}>
                {/* Thread area */}
                <div style={{ maxHeight: 360, overflowY: "auto", padding: 8, border: "1px solid #eee", borderRadius: 8, marginBottom: 12 }}>
                    {loadingThread && <div className="muted pad">Loading conversation…</div>}
                    {!loadingThread && thread.length === 0 && <div className="muted pad">No messages between you and @{toHandle || toUid} yet.</div>}

                    {!loadingThread && thread.map((m, idx) => {
                        const mine = m.from === currentUid;
                        return (
                            <div key={m.id || `msg-${idx}`} className={`mim-bubble ${mine ? "mine" : "theirs"}`} style={{ marginBottom: 8 }}>
                                <div className="mim-bubble-text" style={{ whiteSpace: "pre-wrap" }}>{m.text}</div>
                                <div className="muted small mim-ts" style={{ marginTop: 6 }}>{formatDate(m.createdAt)}</div>
                            </div>
                        );
                    })}
                </div>

                {/* Composer */}
                <form onSubmit={handleSend} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {!user || !user.uid ? (
                        <div style={{ color: "#a00", fontSize: 14 }}>You must be signed in to send a message.</div>
                    ) : null}

                    <textarea
                        ref={composerRef}
                        placeholder={user && user.uid ? "Write your message..." : "Sign in to send a message"}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        style={{ padding: 10, fontSize: 14, minHeight: 100, borderRadius: 6, border: "1px solid #ddd" }}
                        disabled={!user || !user.uid || sending}
                        maxLength={1024}
                    />

                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={() => { setText(""); onClose && onClose(); }}
                            disabled={sending}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn-start" disabled={sending || !user || !user.uid || !toUid}>
                            {sending ? "Sending…" : "Send"}
                        </button>
                    </div>

                    <div style={{ fontSize: 12, color: "#666" }}>
                        {useFirebase ? "Message delivered via the site." : "No backend configured — Send will open your email client."}
                    </div>
                </form>
            </div>
        </Modal>
    );
}

MessageModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    toUid: PropTypes.string,
    toHandle: PropTypes.string,
    db: PropTypes.any,
    user: PropTypes.object,
    useFirebase: PropTypes.bool,
    setToast: PropTypes.func
};
